import { useCallback, RefObject } from 'react';
import { safeParse } from '../utils';
import { STORAGE_FILES, STORAGE_WORKING, STORAGE_BRANCH } from '../constants';
import { useSettings, useUI, usePreviewContext, useEditorState } from '../contexts';

type UseSettingsIOParams = {
  activeProject: string;
  projectOverride: string;
  activeBranch: string;
  setActiveBranch: (value: string) => void;
  setProjectOverride: (value: string) => void;
  applyProjectOverride: (value: string) => string | null;
  setPersistedFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setWorkingFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setFetchedFiles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setCacheVersion: React.Dispatch<React.SetStateAction<number>>;
  importSettingsInputRef: RefObject<HTMLInputElement | null>;
};

export function useSettingsIO({
  activeProject,
  projectOverride,
  activeBranch,
  setActiveBranch,
  setProjectOverride,
  applyProjectOverride,
  setPersistedFiles,
  setWorkingFiles,
  setFetchedFiles,
  setCacheVersion,
  importSettingsInputRef,
}: UseSettingsIOParams) {
  // Get settings from context
  const {
    apiBase,
    apiKey,
    apiSecret,
    autoSaveEnabled,
    setApiBase,
    setApiKey,
    setApiSecret,
    setAutoSaveEnabled,
  } = useSettings();

  // Get UI state from context
  const { sidebarWidth, previewWidth, setSidebarWidth, setPreviewWidth, setStatus } = useUI();

  // Get preview state from context
  const {
    previewDevice,
    customPreviewWidth,
    hotReloadEnabled,
    sandboxedPreview,
    setPreviewDevice,
    setCustomPreviewWidth,
    setHotReloadEnabled,
    setSandboxedPreview,
  } = usePreviewContext();

  // Get editor state from context
  const { setActiveFile, setOpenTabs } = useEditorState();
  const handleApplyProjectOverride = useCallback(
    (next: string) => {
      const applied = applyProjectOverride(next);
      if (!applied) return;
      setPersistedFiles(safeParse<Record<string, string>>(localStorage.getItem(`${STORAGE_FILES}:${applied}`)) || {});
      setWorkingFiles(safeParse<Record<string, string>>(localStorage.getItem(`${STORAGE_WORKING}:${applied}`)) || {});
      setOpenTabs([]);
      setActiveFile(null);
      setFetchedFiles({});
      setCacheVersion((v) => v + 1);
    },
    [applyProjectOverride, setPersistedFiles, setWorkingFiles, setOpenTabs, setActiveFile, setFetchedFiles, setCacheVersion]
  );

  const handleExportSettings = useCallback(() => {
    const payload = {
      apiBase,
      apiKey,
      apiSecret,
      activeProject,
      projectOverride,
      activeBranch,
      autoSaveEnabled,
      sidebarWidth,
      previewWidth,
      previewDevice,
      customPreviewWidth,
      hotReloadEnabled,
      sandboxedPreview,
    };
    try {
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'atlas-settings.json';
      a.click();
      URL.revokeObjectURL(url);
      setStatus('Ayarlar dışa aktarıldı');
    } catch (error) {
      console.error('Export settings error', error);
      setStatus('Export başarısız');
    }
  }, [activeBranch, activeProject, apiBase, apiKey, apiSecret, autoSaveEnabled, customPreviewWidth, hotReloadEnabled, previewDevice, previewWidth, projectOverride, sandboxedPreview, sidebarWidth, setStatus]);

  const handleImportSettings = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(String(reader.result) || '{}');
          if (data.apiBase) setApiBase(data.apiBase);
          if (data.apiKey) setApiKey(data.apiKey);
          if (data.apiSecret) setApiSecret(data.apiSecret);
          if (data.activeProject) handleApplyProjectOverride(data.activeProject);
          if (data.activeBranch) {
            setActiveBranch(data.activeBranch);
            localStorage.setItem(STORAGE_BRANCH, data.activeBranch);
          }
          if (typeof data.autoSaveEnabled === 'boolean') setAutoSaveEnabled(data.autoSaveEnabled);
          if (typeof data.sidebarWidth === 'number') setSidebarWidth(data.sidebarWidth);
          if (typeof data.previewWidth === 'number') setPreviewWidth(data.previewWidth);
          if (data.previewDevice) setPreviewDevice(data.previewDevice);
          if (typeof data.customPreviewWidth === 'number') setCustomPreviewWidth(data.customPreviewWidth);
          if (typeof data.hotReloadEnabled === 'boolean') setHotReloadEnabled(data.hotReloadEnabled);
          if (typeof data.sandboxedPreview === 'boolean') setSandboxedPreview(data.sandboxedPreview);
          if (data.projectOverride) setProjectOverride(data.projectOverride);
          setStatus('Ayarlar içe aktarıldı');
        } catch (error) {
          console.error('Import settings error', error);
          setStatus('Import başarısız');
        } finally {
          event.target.value = '';
        }
      };
      reader.readAsText(file);
    },
    [handleApplyProjectOverride, setActiveBranch, setProjectOverride, setApiBase, setApiKey, setApiSecret, setAutoSaveEnabled, setSidebarWidth, setPreviewWidth, setPreviewDevice, setCustomPreviewWidth, setHotReloadEnabled, setSandboxedPreview, setStatus]
  );

  const triggerImport = useCallback(() => {
    importSettingsInputRef.current?.click();
  }, [importSettingsInputRef]);

  return {
    handleExportSettings,
    handleImportSettings,
    handleApplyProjectOverride,
    triggerImport,
  };
}
